import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

function maskValue(value, mode = 'truncate') {
  if (!value) return '—';

  const str = String(value);

  if (mode === 'email') {
    return str.length > 25 ? `${str.slice(0, 25)}...` : str;
  }

  if (mode === 'code') {
    if (str.length <= 6) return str;
    return `${str.slice(0, 3)}****${str.slice(-2)}`;
  }

  if (mode === 'phone') {
    if (str.length <= 6) return str;
    return `${str.slice(0, 3)}****${str.slice(-2)}`;
  }

  if (str.length <= 25) return str;

  return `${str.slice(0, 25)}...`;
}

export default function RevealTypography({
  value,
  mask = 'truncate',
  tooltip = 'View',
  copyTooltip = 'Copy',
  closeDelay = 1500,
  TypographyProps = {},
  maxWidth = 420
}) {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);

  const maskedValue = useMemo(() => maskValue(value, mask), [value, mask]);

  const requiresReveal = useMemo(() => {
    if (!value) return false;

    const str = String(value);

    if (mask === 'email') {
      return str.length > 25;
    }

    if (mask === 'truncate') {
      return str.length > 25;
    }

    // code, phone, etc. are always masked
    return true;
  }, [value, mask]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));

      setCopied(true);

      setTimeout(() => {
        setCopied(false);

        if (requiresReveal) {
          handleClose();
        }
      }, closeDelay);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const open = Boolean(anchorEl);

  if (!value) {
    return <Typography {...TypographyProps}>—</Typography>;
  }

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography {...TypographyProps}>{maskedValue}</Typography>

        {requiresReveal ? (
          <Tooltip title={tooltip}>
            <IconButton size="small" onClick={handleOpen}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={copied ? 'Copied' : copyTooltip}>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? (
                <CheckOutlinedIcon fontSize="small" color="success" />
              ) : (
                <ContentCopyOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {requiresReveal && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            sx: {
              mt: 1,
              px: 1.5,
              py: 1,
              backdropFilter: 'blur(12px)',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(30,30,30,0.85)'
                  : 'rgba(255,255,255,0.85)',
              border: `1px solid ${theme.palette.divider}`,
              minWidth: 220,
              maxWidth
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-all',
                flex: 1
              }}
            >
              {value}
            </Typography>

            <Tooltip title={copied ? 'Copied' : copyTooltip}>
              <IconButton size="small" onClick={handleCopy}>
                {copied ? (
                  <CheckOutlinedIcon
                    fontSize="small"
                    color="success"
                  />
                ) : (
                  <ContentCopyOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Popover>
      )}
    </>
  );
}

RevealTypography.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  mask: PropTypes.oneOf([
    'truncate',
    'email',
    'code',
    'phone'
  ]),
  tooltip: PropTypes.string,
  copyTooltip: PropTypes.string,
  closeDelay: PropTypes.number,
  TypographyProps: PropTypes.object,
  maxWidth: PropTypes.number
};